
import { createElement, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Play, X } from 'lucide-react';
import { ticketsApi } from '@/shared/api/tickets';
import { IconButton, MediaViewer, Tooltip, toast, type MediaViewerItem } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { attachmentIcon, attachmentKind, formatBytes } from '../lib/attachments';
import styles from './AttachmentPicker.module.css';

export const MAX_PICKER_FILES = 6;
const MAX_FILE_MB = 20;

const PASTE_KEYS =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘V' : 'Ctrl+V';

export interface AttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  pasteHint?: boolean;
}

const isTextField = (el: EventTarget | null) =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable);

export function clipboardFiles(data: DataTransfer | null, target: EventTarget | null): FileList | null {
  if (!data || data.files.length === 0) return null;
  const rich = data.types.includes('text/html') || data.types.includes('text/rtf');
  if (rich && isTextField(target) && data.getData('text/plain').trim() !== '') return null;
  return data.files;
}

const isMediaFile = (f: File) => f.type.startsWith('image/') || f.type.startsWith('video/');

export function appendFiles(current: File[], list: FileList | File[], onChange: (files: File[]) => void) {
  const incoming = Array.from(list);
  const next = [...current];
  for (const file of incoming) {
    if (next.length >= MAX_PICKER_FILES) {
      toast.error(`Не больше ${MAX_PICKER_FILES} вложений`);
      break;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`«${file.name}» больше ${MAX_FILE_MB} МБ`);
      continue;
    }
    next.push(file);
  }
  if (next.length !== current.length) onChange(next);
}

function RowIcon({ kind }: { kind: ReturnType<typeof attachmentKind> }) {
  return (
    <span className={styles.rowIcon} aria-hidden>
      {createElement(attachmentIcon(kind), { size: 18 })}
    </span>
  );
}

export function AttachmentPicker({ files, onChange, disabled, pasteHint }: AttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');

  const [previews, setPreviews] = useState<Map<File, string>>(() => new Map());
  useLayoutEffect(() => {
    const next = new Map(files.filter(isMediaFile).map((f) => [f, URL.createObjectURL(f)] as const));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreviews(next);
    return () => next.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const mediaItems = useMemo<MediaViewerItem[]>(
    () =>
      files.filter(isMediaFile).map((f) => ({
        url: previews.get(f) ?? '',
        fileName: f.name,
        type: f.type.startsWith('video/') ? 'video' : 'image',
      })),
    [files, previews],
  );

  const addFiles = (list: FileList | File[]) => appendFiles(files, list, onChange);

  const removeAt = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const indexed = files.map((file, index) => ({ file, index }));
  const media = indexed.filter(({ file }) => isMediaFile(file));
  const rows = indexed.filter(({ file }) => !isMediaFile(file));
  const full = files.length >= MAX_PICKER_FILES;

  return (
    <div
      className={styles.picker}
      data-dragging={dragging || undefined}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled && !dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {media.length > 0 && (
        <div className={styles.gallery}>
          {media.map(({ file, index }, mediaIndex) => {
            const previewUrl = previews.get(file);
            return (
              <div key={`${file.name}-${index}`} className={styles.thumb} title={file.name}>
                <button
                  type="button"
                  className={styles.thumbBtn}
                  onClick={() => setViewerIndex(mediaIndex)}
                  aria-label={`Открыть «${file.name}»`}
                >
                  {previewUrl &&
                    (file.type.startsWith('video/') ? (
                      <video src={previewUrl} muted preload="metadata" />
                    ) : (
                      <img src={previewUrl} alt={file.name} />
                    ))}
                  {file.type.startsWith('video/') && (
                    <span className={styles.play} aria-hidden>
                      <Play size={16} fill="currentColor" />
                    </span>
                  )}
                </button>
                <Tooltip content="Убрать">
                  <button
                    type="button"
                    className={styles.thumbRemove}
                    onClick={() => removeAt(index)}
                    disabled={disabled}
                    aria-label={`Убрать файл ${file.name}`}
                  >
                    <X size={12} aria-hidden />
                  </button>
                </Tooltip>
              </div>
            );
          })}
        </div>
      )}
      {rows.map(({ file, index }) => (
        <div key={`${file.name}-${index}`} className={styles.row} title={file.name}>
          <RowIcon kind={attachmentKind(file.type, file.name)} />
          <span className={styles.rowInfo}>
            <span className={styles.rowName}>{file.name}</span>
            <span className={styles.rowMeta}>{formatBytes(file.size)}</span>
          </span>
          <IconButton
            label={`Убрать файл ${file.name}`}
            tooltip="Убрать"
            tone="danger"
            size="lg"
            onClick={() => removeAt(index)}
            disabled={disabled}
          >
            <X size={16} aria-hidden />
          </IconButton>
        </div>
      ))}
      { }
      <button
        type="button"
        className={cn(styles.row, styles.add)}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || full}
      >
        <span className={styles.rowIcon} aria-hidden>
          <Paperclip size={18} />
        </span>
        <span className={styles.addLabel}>{files.length === 0 ? 'Прикрепить файлы' : 'Добавить ещё'}</span>
        {files.length > 0 ? (
          <span className={styles.rowMeta}>
            {files.length} из {MAX_PICKER_FILES}
          </span>
        ) : (
          pasteHint &&
          !isMobile && <kbd className={styles.kbd}>{PASTE_KEYS}</kbd>
        )}
      </button>
      {viewerIndex !== null && mediaItems[viewerIndex] && (
        <MediaViewer
          items={mediaItems}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}

export async function uploadDraftAttachments(ticketId: string, files: File[]): Promise<void> {
  if (files.length === 0) return;
  const attachments = await Promise.all(
    files.map((file) => ticketsApi.uploadAttachment(ticketId, file)),
  );
  await ticketsApi.addMessage(ticketId, '', attachments.map((a) => a.id));
}
